import React, { useEffect, useState } from 'react';
import {
  Card,
  Box,
  Typography,
  TablePagination,
  Divider,
  CircularProgress, // Aggiunto per un feedback visivo durante il caricamento
} from '@mui/material';
import { useDashboardFilters } from 'contexts/DashboardFilterContext';
import { buildFilterBody } from './utils';
import axiosInstance from "@/utils/axios";
import { useIntl } from "react-intl";
import { SUPPORTED_SPORTS } from 'constants/globalConstants';

// ─── Types ────────────────────────────────────────────────────────────────────

type SortKey = 
  | 'competitionName'
  | 'season'
  | 'clips'
  | 'goalParade'
  | 'highlights'
  | 'totalPublications'
  | 'totalViews'
  | 'totalLikes'
  | 'totalShares';

type SortDir = 'asc' | 'desc';

interface MatchRow {
  homeTeam: string;
  awayTeam: string;
  competitionName: string;
  season: string;
  clips: number;
  goalParade: number;
  highlights: number;
  totalPublications: number;
  totalViews: number;
  totalLikes: number;
  totalShares: number;
}

// ─── Layout Dimensioni e Allineamenti (Ottimizzato a 1200px) ───────────────────
const colLayout = {
  rank: { width: 50, display: 'flex', justifyContent: 'center', flexShrink: 0 },
  match: { flex: 2, minWidth: 200, display: 'flex', justifyContent: 'flex-start', flexShrink: 0 },
  competitionName: { flex: 0.8, minWidth: 100, display: 'flex', justifyContent: 'flex-start', flexShrink: 0 },
  season: { flex: 0.8, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 },
  clips: { flex: 0.8, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 },
  goalParade: { flex: 1.2, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 },
  highlights: { flex: 1.2, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 },
  totalViews: { flex: 1.2, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 },
  totalLikes: { flex: 1.2, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 },
  totalShares: { flex: 1.2, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 },
};

// ─── Cells ─────────────────────────────────────────────────────────────────────

const RankCell = ({ rank }: { rank: number }) => (
  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.secondary' }}>
    {rank}
  </Typography>
);

const MatchCell = ({ match }: { match: MatchRow }) => (
  <Box display="flex" alignItems="center" gap={1}>
    <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: 'text.primary' }}>
      {match.homeTeam}
    </Typography>
    <Typography sx={{ fontSize: '0.84rem', color: 'text.secondary', px: 0.5 }}>-</Typography>
    <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: 'text.primary' }}>
      {match.awayTeam}
    </Typography>
  </Box>
);

const StatCell = ({ value, align = 'right' }: { value: number | string; align?: 'left' | 'right' }) => (
  <Typography 
    sx={{ 
      fontSize: '0.8125rem', 
      fontWeight: 600, 
      fontVariantNumeric: 'tabular-nums',
      textAlign: align 
    }}
  >
    {typeof value === 'number' 
      ? (formatCompactNumber(value)) 
      : value}
  </Typography>
);

const formatCompactNumber = (value: number): string => {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1).replace('.0', '')}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1).replace('.0', '')}K`;
  }
  return value.toString();
};

// ─── Main Component ────────────────────────────────────────────────────────────

export default function SharedMatchPlatformChart() {
  const [chartData, setChartData] = useState<MatchRow[]>([]);
  const { contentType, competition, season, dateRange, updateDashboard, filterLoaded } = useDashboardFilters();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('totalShares'); 
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const handleSort = (key: SortKey) => {
    setSortDir(prev => sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'desc');
    setSortKey(key);
  };

  const sorted = [...chartData].sort((a, b) => {
    const valA = a[sortKey];
    const valB = b[sortKey];
    
    if (typeof valA === 'string' && typeof valB === 'string') {
      return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    }
    
    const diff = Number(valA ?? 0) - Number(valB ?? 0);
    return sortDir === 'asc' ? diff : -diff;
  });

  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const CURRENT_SPORT = ((import.meta as any).env.VITE_APP_SPORT).toUpperCase();
  const sortableCols = [
    { key: 'competitionName', label: 'Competition', align: 'left' },
    { key: 'season', label: 'Season', align: 'right' },
    { key: 'clips', label: 'Clips', align: 'right' },
    ...( CURRENT_SPORT === SUPPORTED_SPORTS.FOOTBALL ? [{ key: 'goalParade', label: 'Goal Parade', align: 'right' }]:[]),
    { key: 'highlights', label: 'Highlights', align: 'right' },
    { key: 'totalViews', label: 'Views', align: 'right' },
    { key: 'totalLikes', label: 'Likes', align: 'right' },
    { key: 'totalShares', label: 'Shares', align: 'right' },
  ] as const;

  useEffect(() => {
    if (filterLoaded) {
      let cancelled = false;

      const fetchData = async () => {      
        try {
          setIsLoading(true);
          setError(null);

          const body = buildFilterBody(
            contentType ?? [],
            competition ?? [],
            season ?? [],
            dateRange
          );

          let url = '';
          switch(CURRENT_SPORT){
            case SUPPORTED_SPORTS.FOOTBALL:
              url = '/dashboard/getSharedMatchFootballPlatformChart';
              break;
            case SUPPORTED_SPORTS.VOLLEY:
              url = '/dashboard/getSharedMatchVolleyballPlatformChart';
              break;
            default: break;
          }
          const res = await axiosInstance.post(url, body);

          if (!cancelled) {
            setChartData(res.data || []);
            setPage(0); 
          }
        } catch (err) {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : 'Failed to load analytics data');
          }
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      };

      fetchData();
      return () => { cancelled = true; };
    }
  }, [updateDashboard, filterLoaded]);

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px' }} data-tour={"dashboard_match_engagement"}>
      
      {/* HEADER */}
      <Box px={3} pt={2.5} pb={2}>
        <Typography variant="h3" fontWeight={700} sx={{ fontSize: '1.25rem' }}>
          Match Engagement Overview
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          Ranking of matches based on social engagement metrics and video production
        </Typography>
      </Box>

      <Divider />

      {/* CONTENITORE TABELLA SCROLLABILE REATTIVA */}
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Box sx={{ minWidth: 1200 }}>

          {/* HEADERS DELLA TABELLA (Sempre visibili) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 3,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={colLayout.rank}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>#</Typography>
            </Box>
            <Box sx={colLayout.match}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>Match</Typography>
            </Box>

            {sortableCols.map(col => {
              const isActive = sortKey === col.key;
              return (
                <Box
                  key={col.key}
                  sx={{ 
                    ...colLayout[col.key], 
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { color: 'text.primary' }
                  }}
                  onClick={() => handleSort(col.key)}
                >
                  <Typography 
                    sx={{ 
                      fontSize: '0.7rem', 
                      fontWeight: 700, 
                      color: isActive ? 'text.primary' : 'text.secondary',
                      textTransform: 'uppercase',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {col.label} {isActive ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* RIGHE DATI DELLA TABELLA / STATI DI CARICAMENTO E NO-DATA */}
          <Box 
            sx={{ 
              height: 440, 
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {isLoading ? (
              // 1. Stato di Caricamento
              <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flexGrow={1} gap={2}>
                <CircularProgress size={28} thickness={5} />
                <Typography variant="body2" color="text.secondary">
                  Loading data...
                </Typography>
              </Box>
            ) : error ? (
              // 2. Stato Errore API (Opzionale ma consigliato)
              <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1} px={3}>
                <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                  {error}
                </Typography>
              </Box>
            ) : chartData.length === 0 ? (
              // 3. Stato No Data Available (Centrato perfettamente nel box da 440px)
              <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  No data available to display
                </Typography>
              </Box>
            ) : (
              // 4. Render dei Dati Effettivi
              paginated.map((match, idx) => {
                const rank = page * rowsPerPage + idx + 1;

                return (
                  <Box
                    key={`${match.homeTeam}-${match.awayTeam}-${idx}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 3,
                      py: 1.75,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      transition: 'background 0.15s',
                      '&:hover': { bgcolor: 'action.hover' },
                      '&:last-child': { borderBottom: 0 }
                    }}
                  >
                    <Box sx={colLayout.rank}>
                      <RankCell rank={rank} />
                    </Box>

                    <Box sx={colLayout.match}>
                      <MatchCell match={match} />
                    </Box>

                    <Box sx={colLayout.competitionName}>
                      <StatCell value={match.competitionName} align="left" />
                    </Box>

                    <Box sx={colLayout.season}>
                      <StatCell value={match.season} />
                    </Box>

                    <Box sx={colLayout.clips}>
                      <StatCell value={match.clips} />
                    </Box>

                    { CURRENT_SPORT === SUPPORTED_SPORTS.FOOTBALL && <Box sx={colLayout.goalParade}>
                      <StatCell value={match.goalParade} />
                    </Box>}

                    <Box sx={colLayout.highlights}>
                      <StatCell value={match.highlights} />
                    </Box>

                    <Box sx={colLayout.totalViews}>
                      <StatCell value={match.totalViews} />
                    </Box>

                    <Box sx={colLayout.totalLikes}>
                      <StatCell value={match.totalLikes} />
                    </Box>

                    <Box sx={colLayout.totalShares}>
                      <StatCell value={match.totalShares} />
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>

          {/* PAGINAZIONE IN FONDO ALLA CARD */}
          <TablePagination
            component="div"
            count={sorted.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
            onRowsPerPageChange={() => {}}
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          />

        </Box>
      </Box>
    </Card>
  );
}